import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { UserProvider } from '@/contexts/UserContext';
import { CompetitionProvider } from '@/contexts/CompetitionContext';
import { TenantProvider } from '@/contexts/TenantContext';
import { QueryProvider } from '@/providers/QueryProvider';
import { DebugFooter } from '@/components/debug/DebugFooter';
import { OAuthCallback } from '@/components/auth/OAuthCallback';
import { SignInPage, SignUpPage } from '@/components/auth/ClerkAuthPages';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { initializeGPT, enableAndDisplayAds } from '@/utils/gpt';
import { useGetTenant } from '@/hooks/useTenant';
import { getGAMConfigFromTenant } from '@/config/ads.config';
import Index from './pages/Index';
import NotFound from './pages/NotFound';
import EnvVars from './pages/EnvVars';

import {
  resolveTenantFromUrl,
  isUrlTenantMode,
  getTenantBasePath,
} from '@low6-pickem/frontend-shared';

const TENANT_SLUG = resolveTenantFromUrl();
const TENANT_BASE_PATH = getTenantBasePath();

const AppContent = () => {
  const { t } = useTranslation();
  const { isLoading, authError } = useAuth();
  const {
    data: tenant,
    isLoading: isTenantLoading,
    error: tenantError,
  } = useGetTenant(TENANT_SLUG);

  const usesClerk = tenant?.authProvider === 'clerk';

  useEffect(() => {
    if (isTenantLoading || !tenant) {
      return;
    }

    const gamConfig = getGAMConfigFromTenant(tenant);

    if (!gamConfig || !gamConfig.enabled) {
      return;
    }

    const initAds = async () => {
      try {
        await initializeGPT();
        enableAndDisplayAds();
      } catch (error) {
        console.error('[App] Failed to initialize GPT:', error);
      }
    };

    initAds();
  }, [tenant, isTenantLoading]);

  const pathname = window.location.pathname;
  const pathForAuth =
    TENANT_BASE_PATH && pathname.startsWith(TENANT_BASE_PATH)
      ? pathname.slice(TENANT_BASE_PATH.length) || '/'
      : pathname;
  // Only the real /sign-in and /sign-up routes — not e.g. /main/lobby/sign-in
  const isClerkAuthRoute =
    usesClerk &&
    (pathForAuth === '/sign-in' ||
      pathForAuth.startsWith('/sign-in/') ||
      pathForAuth === '/sign-up' ||
      pathForAuth.startsWith('/sign-up/'));

  if (isClerkAuthRoute) {
    return (
      <TooltipProvider>
        <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
          <Routes>
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    );
  }

  if (tenantError) {
    if (isUrlTenantMode()) {
      window.location.href = '/';
      return null;
    }
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">{t('app.configError.title')}</h2>
          <p className="text-gray-400 mb-4">{t('app.configError.description')}</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">{t('app.authFailed.title')}</h2>
          <p className="text-gray-400 mb-4">{authError.message}</p>
        </div>
      </div>
    );
  }

  if (isLoading || isTenantLoading) {
    if (isClerkAuthRoute) {
      return (
        <TooltipProvider>
          <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
            <Routes>
              <Route path="/sign-in/*" element={<SignInPage />} />
              <Route path="/sign-up/*" element={<SignUpPage />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      );
    }

    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-white">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <TenantProvider tenant={tenant}>
      <UserProvider>
        <CompetitionProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/welcome" element={<Index />} />
                <Route path="/how-to-play" element={<Index />} />
                <Route path="/main" element={<Index />} />
                <Route path="/main/:tab" element={<Index />} />
                <Route path="/game/:gameId" element={<Index />} />
                <Route path="/league/:leagueName" element={<Index />} />
                <Route path="/env-vars" element={<EnvVars />} />
                <Route path="/auth/callback" element={<OAuthCallback />} />
                {usesClerk && <Route path="/sign-in/*" element={<SignInPage />} />}
                {usesClerk && <Route path="/sign-up/*" element={<SignUpPage />} />}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
            {import.meta.env.VITE_SHOW_DEBUG_FOOTER === '1' && <DebugFooter />}
          </TooltipProvider>
        </CompetitionProvider>
      </UserProvider>
    </TenantProvider>
  );
};

/**
 * The chrome that outlives the app.
 *
 * The language switcher sits *outside* the app's error boundary, not just above
 * `AuthProvider`: `AppContent` and `AuthProvider` each return early in their
 * loading, error and pre-auth branches, and the boundary replaces the tree
 * outright when anything below it throws. Rendered here it survives all of
 * them — including the crash screen, whose own text is translated.
 *
 * It has a boundary of its own so a fault in the switcher costs a player the
 * switcher, never the game.
 */
export const AppChrome = ({ children }: { children: ReactNode }) => (
  <>
    <ErrorBoundary fallback={() => null}>
      <LanguageSwitcher />
    </ErrorBoundary>
    <ErrorBoundary onError={error => console.error('[App Error]', error)}>{children}</ErrorBoundary>
  </>
);

const App = () => {
  return (
    <AppChrome>
      <QueryProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryProvider>
    </AppChrome>
  );
};

export default App;
